import { useMutation } from '@apollo/client';
import { Edit } from '@mui/icons-material';
import { Box, Dialog, DialogContent, DialogTitle, IconButton, MenuItem } from '@mui/material';
import Typography from '@mui/material/Typography';
import { FormSubmitRow } from 'components/common/form/submit-row';
import { MetadataItem } from 'components/common/metadata';
import { FC, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { LeadStatus } from 'types/apollo/globalTypes';
import { UpdateLeadStatus, UpdateLeadStatusVariables } from 'types/apollo/UpdateLeadStatus';
import {
  UpdateLeadUndeliverableReason,
  UpdateLeadUndeliverableReasonVariables,
} from 'types/apollo/UpdateLeadUndeliverableReason';
import { FormTextField } from '../forms/form-text-field';
import {
  UpdateLeadStatusMutation,
  UpdateLeadUndeliverableReasonMutation,
} from './conversation.graphql';

interface EditLeadStatusDialogProps {
  campaignId: string;
  phone: string;
  currentStatus: string;
  currentUndeliverableReason: string | null;
  onSuccess: () => void;
}

interface FormValues {
  status: LeadStatus | string;
  undeliverableReason: string;
}

const mapStatus = (status: string): LeadStatus | null => {
  switch (status) {
    case 'flow':
      return LeadStatus.FLOW;
    case 'complete':
      return LeadStatus.COMPLETE;
    case 'transfer':
      return LeadStatus.TRANSFER;
    case 'dead':
      return LeadStatus.DEAD;
    case 'end-contact':
      return LeadStatus.END_CONTACT;
    case 'dnc':
      return LeadStatus.DNC;
  }
  return null;
};

export const EditLeadStatusDialog: FC<EditLeadStatusDialogProps> = ({
  campaignId,
  phone,
  currentStatus,
  currentUndeliverableReason,
  onSuccess,
}) => {
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const [undeliverableReasonMessage, setUndeliverableReasonMessage] = useState('');

  const form = useForm<FormValues>({
    defaultValues: {
      status: currentStatus || '',
      undeliverableReason: currentUndeliverableReason || '',
    },
  });

  const [updateLeadStatus] = useMutation<UpdateLeadStatus, UpdateLeadStatusVariables>(
    UpdateLeadStatusMutation,
  );
  const [updateLeadUndeliverableReason] = useMutation<
    UpdateLeadUndeliverableReason,
    UpdateLeadUndeliverableReasonVariables
  >(UpdateLeadUndeliverableReasonMutation);

  const handleOpen = () => {
    form.reset({
      status: currentStatus || '',
      undeliverableReason: currentUndeliverableReason || '',
    });
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    form.reset();
    setStatusMessage('');
    setUndeliverableReasonMessage('');
  };

  const handleSubmit = async (data: FormValues) => {
    let newStatus = currentStatus;
    let newUndeliverableReason = currentUndeliverableReason || '';

    setStatusMessage('');
    setUndeliverableReasonMessage('');

    try {
      const status = mapStatus(data.status);
      if (!status) {
        return;
      }

      setSubmitting(true);
      if (data.status !== currentStatus) {
        const result = await updateLeadStatus({
          variables: {
            input: {
              campaignId,
              leadPhoneNumber: phone,
              newStatus: status,
            },
          },
        });

        const statusResult = result.data?.updateLeadStatus;
        if (statusResult) {
          setStatusMessage(statusResult);
          newStatus = data.status;
          if (statusResult.includes('unset undeliverable reason')) {
            newUndeliverableReason = '';
          }
        }
      }

      // Update undeliverable reason if it has changed
      if (data.undeliverableReason !== currentUndeliverableReason) {
        const result = await updateLeadUndeliverableReason({
          variables: {
            input: {
              campaignId,
              leadPhoneNumber: phone,
              undeliverableReason: data.undeliverableReason || null, // Send null if empty string to clear it
            },
          },
        });

        if (result.data?.updateLeadUndeliverableReason) {
          setUndeliverableReasonMessage(result.data.updateLeadUndeliverableReason);
          newUndeliverableReason = data.undeliverableReason || '';
        }
      }

      if (newStatus || newUndeliverableReason) {
        form.reset({
          status: newStatus,
          undeliverableReason: newUndeliverableReason,
        });
      }

      onSuccess();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <MetadataItem
        label="Status"
        value={currentStatus}
        postText={
          <IconButton onClick={handleOpen}>
            <Edit />
          </IconButton>
        }
      />

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Lead Status</DialogTitle>
        <FormProvider {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)}>
            <DialogContent>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                <FormTextField
                  name="status"
                  form={form}
                  helperText={
                    currentStatus == 'dnc' ? "Lead will be un-dnc'd" : 'Update lead status. '
                  }
                >
                  <MenuItem value="flow">flow</MenuItem>
                  <MenuItem value="complete">complete</MenuItem>
                  <MenuItem value="transfer">transfer</MenuItem>
                  <MenuItem value="dead">dead</MenuItem>
                  <MenuItem value="end-contact">end-contact</MenuItem>
                  <MenuItem value="dnc">dnc</MenuItem>
                </FormTextField>

                <FormTextField
                  form={form}
                  name="undeliverableReason"
                  label="Undeliverable Reason"
                  optional
                  maxLength={20}
                  helperText="Leave empty to clear the undeliverable reason"
                />
              </Box>
              <Typography>{statusMessage}</Typography>
              <Typography>{undeliverableReasonMessage}</Typography>
            </DialogContent>

            <FormSubmitRow
              onCancel={handleClose}
              loading={submitting}
              submitText="Save"
              cancelText="Close"
              enableIfNotDirty={false}
              cancelAlwaysEnabled
            />
          </form>
        </FormProvider>
      </Dialog>
    </>
  );
};
